import { ChildrenProps, useCallback, useMemo, useState } from 'react';
import { addMinutes, isAfter } from 'date-fns';

import { loaders, LoginSuccess, mutations } from 'entities';
import { store } from 'config/storage';

import { AuthContext } from './context';
import { getSavedUserInfo, navigateToLogin, shouldRefreshSession } from './utils';

import { getTimestamp } from 'utils/date';

const TIME_MINUTES = {
  refreshDash: 5,
  refreshToken: 240,
  ignoreCache: 20,
};

function wrapSurveyRefresh<T>(data: T) {
  return {
    ...data,
    lastSurveyRefresh: getTimestamp(),
  };
}

function isSurveyLoading({ lastSurveyRefresh }: LoginSuccess) {
  if (!lastSurveyRefresh) return true;

  const limit = addMinutes(new Date(lastSurveyRefresh), TIME_MINUTES.ignoreCache);

  return isAfter(new Date(), limit);
}

export function AuthProvider({ children }: ChildrenProps) {
  const [userData, setUserData] = useState(getSavedUserInfo);

  const partiallyOverwrite = useCallback((data: Partial<LoginSuccess>) => {
    setUserData((old) => {
      if (!old) return old;

      const updated = { ...old, ...data };

      store.user.set(updated);

      return updated;
    });
  }, []);

  loaders.auth.refreshSession.use({
    params: { refreshToken: userData?.refreshToken || '' },

    disabled: !shouldRefreshSession(userData),

    onLoad: partiallyOverwrite,

    refreshMinutes: TIME_MINUTES.refreshToken,
  });

  loaders.users.dashboard.use({
    disabled: !userData,

    refreshMinutes: TIME_MINUTES.refreshDash,
    refetchOnWindowFocus: userData?.surveys.length ? 'always' : false,

    onLoad: (data) => partiallyOverwrite(wrapSurveyRefresh(data)),
  });

  const deleteSession = useCallback(() => {
    store.user.delete();

    navigateToLogin();
  }, []);

  const logout = mutations.auth.logout.use({
    onSuccess: deleteSession,
  });

  const finishLogin = useCallback((data: LoginSuccess) => {
    const refreshAware = wrapSurveyRefresh(data);

    setUserData(refreshAware);

    store.user.set(refreshAware);
  }, []);

  const updateUser = useCallback(
    (data: Partial<LoginSuccess['user']>) => {
      partiallyOverwrite({ user: { ...store.user.get()!.user, ...data } });
    },
    [partiallyOverwrite],
  );

  const data = useMemo(
    () => ({
      isLogged: !!userData,
      isLoadingSurveys: userData ? isSurveyLoading(userData) : true,
      logout: logout.execute,

      finishLogin,

      data: userData,

      updateUser,

      deleteSession,

      updateData: partiallyOverwrite,
    }),
    [userData, logout, finishLogin, updateUser, deleteSession, partiallyOverwrite],
  );

  return <AuthContext.Provider value={data}>{children}</AuthContext.Provider>;
}
